import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  IconButton,
  Chip,
  SelectChangeEvent,
  Fade,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import TimelineIcon from '@mui/icons-material/Timeline';
import Breadcrumb from 'components/Breadcrumb';
import { Account, AccountsParams, accountsService } from './services/AccountsService';
import { logger } from 'utils/logger';
import apiService from 'services/ApiService';

// Import components from separated files
import AccountsTableHeader, { SortKey } from './components/AccountsTableHeader';
import AccountRow from './components/AccountRow';
import FilterDrawer from './components/FilterDrawer';
import TablePagination from './components/TablePagination';

// Import hooks
import useAccountsFilter from './hooks/useAccountsFilter';

// Import utilities
import { sortKeyToApiParam, formatDate } from './utils/accountUtils';

// Create a component-specific logger
const log = logger.getLogger('AccountsPage');

const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialPage = parseInt(searchParams.get('page') || '0', 10) || 0;
  const initialRowsPerPage = parseInt(searchParams.get('size') || '25', 10) || 25;
  const initialSortBy = (searchParams.get('sortBy') || 'id') as SortKey;
  const initialSortDirection = (searchParams.get('sortDir') || 'asc') as 'asc' | 'desc';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortKey>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Pagination state
  const [page, setPage] = useState<number>(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
  const [paginatedAccounts, setPaginatedAccounts] = useState<Account[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [serverPagination] = useState<boolean>(true);

  // Server-side sorting state
  const [serverSortBy, setServerSortBy] = useState<string>(sortKeyToApiParam[initialSortBy] || 'id');
  const [serverSortDirection, setServerSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Update URL params when pagination/sorting changes
  const updateUrlParams = useCallback((newPage: number, newSize: number, newSortBy: SortKey, newSortDir: 'asc' | 'desc') => {
    const params = new URLSearchParams(searchParams);

    // Update pagination params
    if (newPage > 0) {
      params.set('page', newPage.toString());
    } else {
      params.delete('page');
    }

    if (newSize !== 25) {
      params.set('size', newSize.toString());
    } else {
      params.delete('size');
    }

    // Update sort params
    if (newSortBy !== 'id') {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }

    if (newSortDir !== 'asc') {
      params.set('sortDir', newSortDir);
    } else {
      params.delete('sortDir');
    }

    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Use the filter hook
  const {
    filterState,
    tempState,
    filterDrawerOpen,
    expandedAccordions,
    setFilterDrawerOpen,
    setExpandedAccordions,
    handleLineOfBusinessChange,
    handleEnabledChange,
    handleActiveStatusChange,
    handleAccountNameFilterChange,
    applyFilters,
    clearFilters,
    removeFilter,
  } = useAccountsFilter();

  // Define fetchAccounts as a memoized callback
  const fetchAccounts = useCallback(async (pageNum = 0, pageSize = rowsPerPage) => {
    try {
      setLoading(true);
      log.log(`Fetching accounts data from API (page ${pageNum}, size ${pageSize}, sort ${serverSortBy}, direction ${serverSortDirection})...`);
      
      // Prepare parameters for the AccountsService
      const params: AccountsParams = {
        page: pageNum,
        size: pageSize,
        sort: serverSortBy,
        direction: serverSortDirection,
        filters: {
          lineOfBusiness: filterState.lineOfBusiness,
          enabled: filterState.enabled,
          accountName: filterState.accountName,
          active: filterState.activeStatus === 'inactive' ? false : undefined,
        },
      };
      
      // Call the AccountsService instead of direct API call
      const result = await accountsService.getAccounts(params);
      
      log.log(`Found ${result.data.length} accounts on page ${pageNum+1} (total: ${result.count})`);
      
      setAccounts(result.data);
      setFilteredAccounts(result.data);
      setTotalItems(result?.count);
      setError(null);
      
    } catch (err) {
      log.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, serverSortBy, serverSortDirection, filterState.lineOfBusiness, filterState.enabled, filterState.accountName, filterState.activeStatus]);

  // Fetch accounts on component mount and when relevant state changes
  useEffect(() => {
    // Invalidate cache for accounts list to ensure we get fresh data
    apiService.invalidateCacheByPattern('/accounts');
    fetchAccounts(page, rowsPerPage);
  }, [fetchAccounts, page, rowsPerPage]);

  // Handle search/filter functionality (client-side only for now)
  useEffect(() => {
    if (!serverPagination) {
      if (searchTerm.trim() === '') {
        setFilteredAccounts(accounts);
      } else {
        const lowercasedFilter = searchTerm.toLowerCase();
        const filtered = accounts.filter(account => 
          account.name.toLowerCase().includes(lowercasedFilter) ||
          account.lineOfBusiness.toLowerCase().includes(lowercasedFilter)
        );
        setFilteredAccounts(filtered);
      }
    }
  }, [searchTerm, accounts, serverPagination]);

  // Handle sorting
  const handleSort = useCallback((key: SortKey) => {
    const isAsc = sortBy === key && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';

    setSortDirection(newDirection);
    setSortBy(key);

    if (serverPagination) {
      // Update server-side sort parameters and trigger a new API call
      const apiSortParam = sortKeyToApiParam[key];
      setServerSortBy(apiSortParam);
      setServerSortDirection(newDirection);
      // Reset to first page when changing sort
      setPage(0);
      // Update URL params
      updateUrlParams(0, rowsPerPage, key, newDirection);
      // Fetch will be triggered by useEffect when sort state changes
    } else {
      // Client-side sorting logic for when not using server pagination
      const sortedAccounts = [...filteredAccounts].sort((a, b) => {
        if (key === 'lastUpdated') {
          return sortDirection === 'asc'
            ? new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()
            : new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
        }

        if (key === 'enabled') {
          return sortDirection === 'asc'
            ? Number(a.strategyPortalEnabled) - Number(b.strategyPortalEnabled)
            : Number(b.strategyPortalEnabled) - Number(a.strategyPortalEnabled);
        }

        const valueA = String(a[key]).toLowerCase();
        const valueB = String(b[key]).toLowerCase();

        if (sortDirection === 'asc') {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      });
      setFilteredAccounts(sortedAccounts);
      updateUrlParams(page, rowsPerPage, key, newDirection);
    }
  }, [sortBy, sortDirection, serverPagination, filteredAccounts, rowsPerPage, page, updateUrlParams]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 0 && (serverPagination || newPage * rowsPerPage < totalItems)) {
      setPage(newPage);
      updateUrlParams(newPage, rowsPerPage, sortBy, sortDirection);
      // The fetchAccounts will be triggered by the useEffect
    }
  }, [serverPagination, rowsPerPage, totalItems, sortBy, sortDirection, updateUrlParams]);

  // Update paginated accounts for client-side pagination
  useEffect(() => {
    if (!serverPagination) {
      // Only do client-side pagination if serverPagination is false
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setPaginatedAccounts(filteredAccounts.slice(startIndex, endIndex));
    } else {
      // For server pagination, filteredAccounts already contains just this page
      setPaginatedAccounts(filteredAccounts);
    }
  }, [filteredAccounts, page, rowsPerPage, serverPagination]);

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event: SelectChangeEvent<number>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing items per page
    updateUrlParams(0, newRowsPerPage, sortBy, sortDirection);
    // Fetch will be triggered by useEffect
  }, [sortBy, sortDirection, updateUrlParams]);

  // Pagination info text
  const getPaginationInfo = useCallback(() => {
    if (filteredAccounts.length === 0) {
      return '0 items';
    }
    
    const startItem = serverPagination 
      ? page * rowsPerPage + 1 
      : Math.min(page * rowsPerPage + 1, totalItems);
    
    const endItem = serverPagination 
      ? Math.min(page * rowsPerPage + filteredAccounts.length, totalItems)
      : Math.min((page + 1) * rowsPerPage, totalItems);
    
    return `${startItem} - ${endItem} of ${totalItems} items`;
  }, [filteredAccounts.length, serverPagination, page, rowsPerPage, totalItems]);

  // Handle edit button click
  const handleEdit = useCallback((accountId: number) => {
    navigate(`/accounts/${accountId}/edit`);
  }, [navigate]);

  // Handle create account button click
  const handleCreateAccount = useCallback(() => {
    navigate('/accounts/create');
  }, [navigate]);

  // Memoize table content to avoid unnecessary re-renders
  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">Loading...</TableCell>
        </TableRow>
      );
    }
    
    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ color: 'error.main' }}>
            {error}
          </TableCell>
        </TableRow>
      );
    }
    
    if (paginatedAccounts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">No accounts found</TableCell>
        </TableRow>
      );
    }
    
    return paginatedAccounts.map((account) => (
      <AccountRow 
        key={account.id} 
        account={account} 
        handleEdit={handleEdit} 
        formatDate={formatDate}
      />
    ));
  }, [paginatedAccounts, loading, error, handleEdit]);

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb 
        items={[
          {
            label: 'Accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />
      
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          color: theme.palette.primary.main,
          mb: 3
        }}
      >
        Accounts
      </Typography>
      
      <Fade in={true} timeout={600}>
        <Paper 
          elevation={2}
          sx={{ 
            width: '100%', 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box
            p={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box display="flex" alignItems="center">
              <Box display="flex" flexWrap="wrap" alignItems="center" mr={1}>
                {filterState.activeFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={filter}
                    size="small"
                    onDelete={() => removeFilter(filter)}
                    sx={{
                      mr: 0.7,
                      mb: 0.5,
                      borderRadius: '16px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  />
                ))}
              </Box>
              <Tooltip title="Filter accounts">
                <IconButton
                  size="medium"
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    ml: 0.5,
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                  }}
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleCreateAccount}
                startIcon={<AddIcon />}
                sx={{
                  ml: 1.5,
                  px: 2,
                  py: 1,
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.14)',
                  },
                }}
              >
                Create Account
              </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                color="warning"
                size="medium"
                onClick={() => navigate('/accounts/inactivity-center')}
                startIcon={<TimelineIcon />}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  },
                }}
              >
                Inactivity Center
              </Button>
            </Box>
          </Box>

          <FilterDrawer
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            tempState={tempState}
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            handleLineOfBusinessChange={handleLineOfBusinessChange}
            handleEnabledChange={handleEnabledChange}
            handleActiveStatusChange={handleActiveStatusChange}
            handleAccountNameFilterChange={handleAccountNameFilterChange}
            applyFilters={applyFilters}
            clearFilters={clearFilters}
          />

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <AccountsTableHeader 
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSort={handleSort}
              />
              <TableBody
                sx={{
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: alpha(theme.palette.background.default, 0.4),
                  }
                }}
              >
                {tableContent}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination 
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            paginatedAccounts={paginatedAccounts}
            serverPagination={serverPagination}
            handlePageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            getPaginationInfo={getPaginationInfo}
          />
        </Paper>
      </Fade>
    </Container>
  );
};

export default AccountsPage;